import React from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { stripShopifyGid, toShopifyProductGid } from "@codway-dev/shared";
import {
  CollapsibleSectionCard,
  SectionHeader,
  ProductPickerButton,
} from "../../../components/shared/EditorShared";
import type { BundleType } from "../../../store/bundle-editor.mapping";
import type { TriggerProduct } from "../../../store/triggers.mapping";

// ─── Section 1: Name ──────────────────────────────────────────────────────────

export function NameSection({
  name,
  onNameChange,
}: {
  name: string;
  onNameChange: (v: string) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={1} title="Información básica" required />}
    >
      <s-stack direction="block" gap="base">
        <s-text color="subdued">
          Asígnale un nombre interno para identificar la oferta en tu panel.
        </s-text>
        <s-text-field
          label="Nombre de la oferta"
          value={name}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onNameChange(e.currentTarget.value)
          }
        />
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 2: Trigger products ──────────────────────────────────────────────

async function openTriggerProductsPicker(
  currentProducts: TriggerProduct[],
  onSelect: (products: TriggerProduct[]) => void
) {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: true,
      selectionIds: currentProducts.map((p) => ({
        id: toShopifyProductGid(p.id),
      })),
    });
    if (!selected) return;
    onSelect(
      selected.map((p) => ({
        id: stripShopifyGid(p.id),
        title: p.title,
        imageUrl: p.images[0]?.originalSrc,
      }))
    );
  } catch {
    window.shopify?.toast?.show("No se pudo abrir el selector de productos.", {
      isError: true,
    });
  }
}

export function TriggerProductSection({
  products,
  onProductsChange,
}: {
  products: TriggerProduct[];
  onProductsChange: (products: TriggerProduct[]) => void;
}) {
  const removeProduct = (id: string) =>
    onProductsChange(products.filter((p) => p.id !== id));

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={2} title="Trigger product" required />}
    >
      <s-stack direction="block" gap="base">
        <s-text color="subdued">
          El bundle se mostrará cuando los clientes vean este producto.
        </s-text>

        <ProductPickerButton
          label={
            products.length > 0
              ? `${products.length} producto(s) seleccionado(s)`
              : "Seleccionar productos"
          }
          onClick={() => openTriggerProductsPicker(products, onProductsChange)}
        />

        {products.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
          >
            {p.imageUrl ? (
              <img
                src={p.imageUrl}
                alt={p.title}
                className="w-10 h-10 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                <s-icon type="image" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {p.title}
              </p>
            </div>
            <button
              onClick={() => removeProduct(p.id)}
              className="text-red-500 hover:text-red-700 flex-shrink-0"
              aria-label="Quitar producto"
            >
              <s-icon type="delete" />
            </button>
          </div>
        ))}
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 3: Bundle type switch ────────────────────────────────────────────

function BundleTypeCard({
  selected,
  title,
  description,
  icon,
  onSelect,
}: {
  selected: boolean;
  title: string;
  description: string;
  icon: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-sm font-medium text-center ${
        selected
          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
      }`}
      aria-pressed={selected}
    >
      {selected && (
        <span className="absolute top-2 right-2 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center">
          <svg
            className="w-2.5 h-2.5 text-white"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path
              d="M3.5 7.5 L5.5 9.5 L8.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      <div className="w-10 h-10 flex items-center justify-center text-gray-400">
        {icon}
      </div>
      <span className="font-semibold">{title}</span>
      <span className="text-xs text-gray-500 font-normal">{description}</span>
    </button>
  );
}

function ProductBundleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
      <path
        d="M3 7l9-4 9 4M3 7l9 4 9-4M3 7v10l9 4 9-4V7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MixMatchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
      <rect
        x="3"
        y="3"
        width="8"
        height="8"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="13"
        y="3"
        width="8"
        height="8"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="3"
        y="13"
        width="8"
        height="8"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="13"
        y="13"
        width="8"
        height="8"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

export function BundleTypeSection({
  bundleType,
  hasContent,
  onChange,
}: {
  bundleType: BundleType;
  // True when the merchant already has items or groups configured —
  // switching the type would wipe them, so the action is gated by a
  // confirm dialog.
  hasContent: boolean;
  onChange: (type: BundleType) => void;
}) {
  const handleSelect = (next: BundleType) => {
    if (next === bundleType) return;
    if (hasContent) {
      const ok = window.confirm(
        "Cambiar el tipo de bundle eliminará los items o grupos configurados. ¿Deseas continuar?"
      );
      if (!ok) return;
    }
    onChange(next);
  };

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={3} title="Elige el tipo de bundle" />}
    >
      <s-stack direction="block" gap="base">
        <s-text color="subdued">
          Selecciona cómo deseas configurar tu bundle.
        </s-text>

        <div className="grid grid-cols-2 gap-3">
          <BundleTypeCard
            selected={bundleType === "fixed"}
            title="Product Bundle"
            description="Combina varios productos en un único paquete a precio especial."
            icon={<ProductBundleIcon />}
            onSelect={() => handleSelect("fixed")}
          />
          <BundleTypeCard
            selected={bundleType === "configurable"}
            title="Mix & Match"
            description="Deja que el cliente arme su bundle eligiendo entre opciones."
            icon={<MixMatchIcon />}
            onSelect={() => handleSelect("configurable")}
          />
        </div>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
